import styled, { css, keyframes } from 'styled-components';

interface SidebarProps {
  isSidebarOpen?: boolean;
  isPathSelected?: boolean;
}

const expandIn = keyframes`
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    max-height: 500px;
    transform: translateY(0);
  }
`;

const SidebarItemGeneric = css<SidebarProps>`
  min-height: 3.25rem;
  display: flex;
  align-items: center;
  position: relative;
  border-radius: 0.5rem;
  margin: 2px 0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${(p) => p.theme.colors.backgroundLight}30;
  }
  
  &:active {
    transform: scale(0.98);
  }

  #sidebar-item-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    flex-shrink: 0;
    margin: ${(p) => (p.isSidebarOpen ? '0' : 'auto')};
    border-radius: 0.375rem;
    transition: all 0.2s ease;
    
    > svg {
      width: 1.35rem;
      height: 1.35rem;
      color: ${(p) => p.theme.colors.textLight};
      transition: color 0.2s ease;
    }
  }

  #sidebar-item-title {
    flex: 1;
    min-width: 0;
    margin-left: 0.5rem;
    display: ${(p) => (p.isSidebarOpen ? 'block' : 'none')};
    
    > p {
      margin: 0;
      font-size: 1.2rem;
      font-weight: 500;
      color: ${(p) => p.theme.colors.text};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: color 0.2s ease;
    }
  }

  #sidebar-item-arrow {
    display: ${(p) => (p.isSidebarOpen ? 'flex' : 'none')}; 
    align-items: center;
    justify-content: center;
    margin-right: 0.25rem;
    transition: transform 0.2s ease;
    
    > svg {
      width: 1.35rem;
      height: 1.35rem;
      color: ${(p) => p.theme.colors.textLight};
    }
  }
  
  /* Hover state */
  &:hover {
    #sidebar-item-icon > svg {
      color: ${(p) => p.theme.colors.primary};
    }
    
    #sidebar-item-title > p {
      color: ${(p) => p.theme.colors.primary};
    }
  }
`;

export const SidebarItemContainer = styled.div<SidebarProps>`
  ${SidebarItemGeneric}

  padding: 0.375rem 0.5rem;
  background: transparent;
  
  ${(p) =>
    p.isPathSelected &&
    css`
      background: linear-gradient(
        90deg,
        ${p.theme.colors.primary}15 0%,
        ${p.theme.colors.primary}08 100%
      );
      
      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 60%;
        background: ${p.theme.colors.primary};
        border-radius: 0 2px 2px 0;
      }
      
      #sidebar-item-icon > svg {
        color: ${p.theme.colors.primary};
      }
      
      #sidebar-item-title > p {
        color: ${p.theme.colors.primary};
        font-weight: 600;
      }
    `};

  &:hover {
    background: ${(p) => p.isPathSelected
    ? `linear-gradient(90deg, ${p.theme.colors.primary}20 0%, ${p.theme.colors.primary}10 100%)`
    : `${p.theme.colors.backgroundLight}30`};
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: ${(p) => p.isPathSelected ? '60%' : '40%'};
      background: ${(p) => p.theme.colors.primary};
      border-radius: 0 2px 2px 0;
      transition: height 0.2s ease;
    }
  }
`;

export const SubItemsContainer = styled.div<{ isOpen?: boolean; }>`
  overflow: hidden;
  animation: ${expandIn} 0.25s ease-out;
  padding-left: 0.5rem;
  margin-left: 0.75rem;
  border-left: 1px solid ${(p) => p.theme.colors.backgroundLight}40;
`;

export const SidebarSubItemContainer = styled.div<SidebarProps>`
  ${SidebarItemGeneric}

  padding: 0.375rem 0.5rem;
  min-height: 3rem;
  background: transparent;
  margin-left: 0.25rem;
  
  #sidebar-item-icon {
    width: 2rem;
    height: 2rem;
    
    > svg {
      width: 1.1rem;
      height: 1.1rem;
    }
  }
  
  #sidebar-item-title > p {
    font-size: 1.1rem;
    font-weight: 400;
  }

  ${(p) =>
    p.isPathSelected &&
    css`
      background: ${p.theme.colors.primary}10;
      
      &::before {
        content: '';
        position: absolute;
        left: -0.75rem;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: 50%;
        background: ${p.theme.colors.primary};
        border-radius: 2px;
      }
      
      #sidebar-item-icon > svg {
        color: ${p.theme.colors.primary};
      }
      
      #sidebar-item-title > p {
        color: ${p.theme.colors.primary};
        font-weight: 500;
      }
    `};

  &:hover {
    background: ${(p) => p.theme.colors.backgroundLight}20;
    
    #sidebar-item-icon > svg {
      color: ${(p) => p.theme.colors.primary};
    }
    
    #sidebar-item-title > p {
      color: ${(p) => p.theme.colors.primary};
    }
  }

  /* Esconder ícone quando sidebar está aberto para subitems */
  ${(p) =>
    p.isSidebarOpen &&
    css`
      #sidebar-item-icon {
        display: none;
      }
      
      #sidebar-item-title {
        margin-left: 0;
      }
    `}
`;
